// src/stations/mock/mockExam.ts — Full-Length Bexley Mock Exam Engine (Story 12.1, FR-MOCK-001..003)
// Simulates the official Bexley Selection Test format:
// 1. Paper 1: Verbal Ability & English Comprehension (50 mins, 50 questions)
// 2. 10-Minute Restorative Break (breathing exercise & hydration reminder)
// 3. Paper 2: Numerical Reasoning & Non-Verbal/Spatial Reasoning (50 mins, 50 questions)
// Strict No-Backtracking Rules: once a paper is submitted, it is sealed forever.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import { AppShell } from '@ui/shell';
import { DiscTimer } from '@ui/components/discTimer';
import { OmrSheet } from '@ui/components/omrSheet';
import { createMockQuestionPool, type MockQuestion } from './mockQuestionPool';
import {
  MockExamStorage,
  type MockExamRecord,
} from '@storage/mockExamStorage';
import { StreakManager } from '@storage/streakManager';
import { TelemetryCollector } from '@storage/telemetryCollector';
import type { ClaweyHud } from '@ui/components/claweyHud';

export type MockExamState =
  | 'lobby'
  | 'paper1_active'
  | 'break'
  | 'paper2_active'
  | 'results';

export interface MockExamOptions {
  hud?: ClaweyHud;
  paperDurationSeconds?: number; // Default 3000s (50 min, FR-MOCK-001)
  breakDurationSeconds?: number; // Default 600s (10 min)
}

export class MockExamStation {
  private container: HTMLElement;
  private hud?: ClaweyHud;
  private paperDurationSeconds: number;
  private breakDurationSeconds: number;

  private state: MockExamState = 'lobby';
  private pool: { paper1: MockQuestion[]; paper2: MockQuestion[] };

  // Current session state
  private currentPaper: 'paper1' | 'paper2' = 'paper1';
  private currentQuestionIndex: number = 0;
  private paper1Answers: Record<string, number | null> = {};
  private paper2Answers: Record<string, number | null> = {};

  // Strict No-Backtrack section enforcement (FR-MOCK-001, T-12.1.2)
  private isPaper1Locked: boolean = false;
  private isPaper2Locked: boolean = false;

  // Timers
  private paperTimerRemaining: number = 0;
  private paperTimerInterval: ReturnType<typeof setInterval> | null = null;
  private breakTimerRemaining: number = 0;
  private breakTimerInterval: ReturnType<typeof setInterval> | null = null;
  private discTimer: DiscTimer | null = null;
  private isZenMode: boolean = false;

  // Timing tracking
  private paper1StartTime: number = 0;
  private paper1TimeSpentSeconds: number = 0;
  private paper2StartTime: number = 0;
  private paper2TimeSpentSeconds: number = 0;
  private questionStartTime: number = 0;

  // OMR Sheet component instance
  private activeOmrSheet: OmrSheet | null = null;

  // Evaluated record
  private currentRecord: MockExamRecord | null = null;

  constructor(options: MockExamOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'mock-exam-container';
    this.container.className = 'mock-exam-station';

    this.hud = options.hud;
    this.paperDurationSeconds = options.paperDurationSeconds ?? 3000; // 50 mins
    this.breakDurationSeconds = options.breakDurationSeconds ?? 600;  // 10 mins

    this.pool = createMockQuestionPool();
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getState(): MockExamState {
    return this.state;
  }

  isSectionLocked(paper: 'paper1' | 'paper2'): boolean {
    return paper === 'paper1' ? this.isPaper1Locked : this.isPaper2Locked;
  }

  getPaper1Answers(): Record<string, number | null> {
    return { ...this.paper1Answers };
  }

  getPaper2Answers(): Record<string, number | null> {
    return { ...this.paper2Answers };
  }

  getRecord(): MockExamRecord | null {
    return this.currentRecord;
  }

  render(): HTMLElement {
    this.container.innerHTML = '';

    if (this.state === 'lobby') {
      this.renderLobby();
    } else if (this.state === 'paper1_active' || this.state === 'paper2_active') {
      this.renderActivePaper();
    } else if (this.state === 'break') {
      this.renderBreakScreen();
    } else if (this.state === 'results') {
      this.renderResultsScreen();
    }

    return this.container;
  }

  /** Stage 1: Official Mock Exam Lobby */
  private renderLobby(): void {
    AppShell.setFocusMode(false);

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="max-width: 840px; margin: 20px auto; padding: 40px 32px; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.14);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 14px; margin-bottom: 20px;">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              🏛️ Official Bexley 11+ Selection Test Simulator
            </div>
            <h1 style="font-family: ${tokens.font.heading}; font-size: 2.4rem; color: ${tokens.color.morrisIndigo}; margin: 6px 0 10px;">
              Full-Length Mock Examination
            </h1>
            <p style="font-size: 1.1rem; line-height: 1.6; color: #3A4D66; max-width: 640px; margin: 0;">
              Experience the genuine pressure and format of the Bexley Selection Test under exact timed conditions.
            </p>
          </div>
          <div style="font-size: 3.5rem;">📜</div>
        </div>

        <!-- 3-Stage Exam Lifecycle Banner -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin: 28px 0; text-align: left;">
          <div style="padding: 20px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 4px;">
              Paper 1 · 50 Mins
            </div>
            <h3 style="margin: 0 0 6px; font-size: 1.2rem; color: ${tokens.color.morrisIndigo};">
              Verbal & English
            </h3>
            <p style="margin: 0; font-size: 0.9rem; color: #5A6D86; line-height: 1.45;">
              50 multiple-choice questions covering comprehension, SPAG, and verbal reasoning.
            </p>
          </div>

          <div style="padding: 20px; background: rgba(255, 246, 229, 0.85); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.gildedBrass}; text-transform: uppercase; margin-bottom: 4px;">
              Mid-Exam Rest · 10 Mins
            </div>
            <h3 style="margin: 0 0 6px; font-size: 1.2rem; color: ${tokens.color.morrisIndigo};">
              Restorative Break
            </h3>
            <p style="margin: 0; font-size: 0.9rem; color: #5A6D86; line-height: 1.45;">
              Guided breathing exercise, stretch, and hydration. Paper 1 is sealed.
            </p>
          </div>

          <div style="padding: 20px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 4px;">
              Paper 2 · 50 Mins
            </div>
            <h3 style="margin: 0 0 6px; font-size: 1.2rem; color: ${tokens.color.morrisIndigo};">
              Maths & Spatial NVR
            </h3>
            <p style="margin: 0; font-size: 0.9rem; color: #5A6D86; line-height: 1.45;">
              50 questions covering numerical multi-step puzzles, 3D nets, and matrix patterns.
            </p>
          </div>
        </div>

        <!-- Strict Exam Rules Callout -->
        <div style="padding: 18px 24px; background: rgba(250, 246, 237, 0.95); border-left: 4px solid ${tokens.color.morrisIndigo}; border-radius: 10px; margin-bottom: 30px; text-align: left;">
          <div style="font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 6px;">
            ⚠️ Official Examination Protocol (No Backtracking):
          </div>
          <ul style="margin: 0; padding-left: 20px; font-size: 0.95rem; color: #3A4D66; line-height: 1.6;">
            <li>Once Paper 1 is submitted, it is <strong>locked permanently</strong>. You cannot return to it.</li>
            <li>Fill answers using the digital <strong>OMR Bubble Sheet</strong> (A, B, C, D, E) or direct click.</li>
            <li>Qualifying Benchmark: Aggregate SAS of <strong>216+</strong> across both papers.</li>
          </ul>
        </div>

        <!-- Start CTA -->
        <button
          id="btn-start-mock-exam"
          class="btn-primary touch-target"
          style="padding: 16px 42px; font-size: 1.25rem; font-weight: 800; border-radius: 16px;"
        >
          Break Seals & Begin Paper 1 &rarr;
        </button>
      </section>
    `;

    const startBtn = this.container.querySelector<HTMLButtonElement>('#btn-start-mock-exam');
    startBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.startPaper('paper1');
    });

    if (this.hud) {
      this.hud.say('Steady breath, Tudor. Sit upright, pace yourself, and trust your training.', 'thinking');
    }
  }

  /** Start Paper 1 or Paper 2 */
  startPaper(paper: 'paper1' | 'paper2'): void {
    if (paper === 'paper1' && this.isPaper1Locked) return;
    if (paper === 'paper2' && this.isPaper2Locked) return;

    this.currentPaper = paper;
    this.currentQuestionIndex = 0;
    this.state = paper === 'paper1' ? 'paper1_active' : 'paper2_active';
    this.paperTimerRemaining = this.paperDurationSeconds;
    this.questionStartTime = Date.now();

    if (paper === 'paper1') {
      this.paper1StartTime = Date.now();
    } else {
      this.paper2StartTime = Date.now();
    }

    // Initialize DiscTimer
    if (this.discTimer) {
      this.discTimer.destroy();
    }
    this.discTimer = new DiscTimer({
      durationSeconds: this.paperDurationSeconds,
      size: 48,
      callbacks: {
        onExpire: () => {
          this.handlePaperTimerExpire();
        },
      },
    });

    this.startPaperCountdown();
    this.render();

    if (this.hud) {
      const name = paper === 'paper1' ? 'Paper 1 (Verbal & English)' : 'Paper 2 (Maths & Spatial NVR)';
      this.hud.say(`${name} is now open! Track your time on the visual disc.`, 'idle');
    }
  }

  private startPaperCountdown(): void {
    if (this.paperTimerInterval) {
      clearInterval(this.paperTimerInterval);
    }
    this.discTimer?.start();

    this.paperTimerInterval = setInterval(() => {
      if (this.paperTimerRemaining > 0) {
        this.paperTimerRemaining -= 1;
        this.updateTimerDisplay();
      } else {
        this.handlePaperTimerExpire();
      }
    }, 1000);
  }

  private updateTimerDisplay(): void {
    const timerEl = this.container.querySelector<HTMLElement>('#mock-timer-display');
    if (!timerEl) return;

    if (this.isZenMode) {
      timerEl.textContent = '🧘 Zen Focus';
    } else {
      const mins = Math.floor(this.paperTimerRemaining / 60);
      const secs = this.paperTimerRemaining % 60;
      timerEl.textContent = `⏱️ ${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
  }

  private handlePaperTimerExpire(): void {
    if (this.paperTimerInterval) {
      clearInterval(this.paperTimerInterval);
      this.paperTimerInterval = null;
    }
    soundEngine.playSuccess();

    if (this.currentPaper === 'paper1') {
      this.submitPaper1(true);
    } else {
      this.submitPaper2(true);
    }
  }

  /** Stage 2: Active Exam Paper with OMR Answer Sheet */
  private renderActivePaper(): void {
    AppShell.setFocusMode(true);

    const questions = this.currentPaper === 'paper1' ? this.pool.paper1 : this.pool.paper2;
    const q = questions[this.currentQuestionIndex];
    if (!q) return;

    const answers = this.currentPaper === 'paper1' ? this.paper1Answers : this.paper2Answers;
    const selectedOptionIndex = answers[q.id];
    const letters = ['A', 'B', 'C', 'D', 'E'];

    this.container.innerHTML = `
      <!-- Top Exam Control Bar -->
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; padding: 12px 18px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.morrisIndigo}; border-radius: 16px; box-shadow: 0 4px 16px rgba(7, 20, 36, 0.08);">
        <div style="display: flex; align-items: center; gap: 10px;">
          <span style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1.1rem;">
            ${this.currentPaper === 'paper1' ? 'Paper 1: Verbal Ability & English' : 'Paper 2: Maths & Spatial NVR'}
          </span>
          <span style="font-size: 0.85rem; padding: 3px 10px; border-radius: 12px; background: rgba(229, 169, 60, 0.2); color: ${tokens.color.morrisIndigo}; font-weight: 800;">
            ${q.sectionName} · Q${this.currentQuestionIndex + 1} of ${questions.length}
          </span>
        </div>

        <div style="display: flex; align-items: center; gap: 10px;">
          <!-- Zen Mode Toggle -->
          <button id="mock-zen-toggle" class="touch-target" style="padding: 6px 14px; font-size: 0.9rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px; background: ${this.isZenMode ? '#E5A93C' : '#FFFDF9'}; color: ${tokens.color.morrisIndigo}; font-weight: 800;" title="Toggle Zen Mode">
            ${this.isZenMode ? '🧘 Zen: ON' : '⏱️ Zen: OFF'}
          </button>

          <!-- Disc Timer Mount -->
          <div id="mock-disc-mount" style="display: flex; align-items: center; justify-content: center; min-width: 48px; min-height: 48px;"></div>

          <!-- Digital Timer Display -->
          <div id="mock-timer-display" style="font-family: ${tokens.font.mono}; font-size: 1.15rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; min-width: 90px; text-align: center;">
            ⏱️ 50:00
          </div>

          <!-- Submit Section CTA -->
          <button id="btn-submit-paper" class="touch-target btn-primary" style="padding: 8px 18px; font-size: 0.95rem; font-weight: 800;">
            ${this.currentPaper === 'paper1' ? 'Seal Paper 1 &rarr;' : 'Finish Mock Exam &rarr;'}
          </button>
        </div>
      </div>

      <!-- Two-Column Stage: Left = Question Card, Right = OMR Bubble Sheet -->
      <div style="display: grid; grid-template-columns: 1fr 340px; gap: 20px; align-items: flex-start;">
        <!-- Question Stage Card (Single Question Focus) -->
        <section class="morris-card" style="padding: 32px 28px; position: relative;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 0.85rem; padding: 4px 12px; border-radius: 12px; background: rgba(7, 20, 36, 0.08); color: ${tokens.color.morrisIndigo}; font-weight: 800; text-transform: uppercase;">
              ${q.topic}
            </span>
            <span style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-style: italic;">
              Select one option (A–E)
            </span>
          </div>

          <h2 style="font-size: 1.45rem; line-height: 1.5; color: ${tokens.color.morrisIndigo}; margin: 0 0 16px;">
            ${q.prompt}
          </h2>

          ${q.contextText ? `
            <div style="padding: 16px 20px; background: rgba(255, 253, 249, 0.9); border-left: 4px solid ${tokens.color.gildedBrass}; margin-bottom: 20px; font-style: italic; font-size: 1.05rem; line-height: 1.6; color: #2D3E50;">
              ${q.contextText}
            </div>
          ` : ''}

          <!-- Options Grid -->
          <div style="display: flex; flex-direction: column; gap: 12px; margin-top: 20px;">
            ${q.options.map((opt, idx) => {
              const isSelected = selectedOptionIndex === idx;
              return `
                <button
                  type="button"
                  class="mock-option-btn touch-target"
                  data-option-idx="${idx}"
                  style="
                    padding: 16px 20px;
                    text-align: left;
                    font-size: 1.1rem;
                    font-weight: 700;
                    line-height: 1.4;
                    border-radius: 14px;
                    border: 2px solid ${isSelected ? tokens.color.morrisIndigo : 'rgba(7, 20, 36, 0.15)'};
                    background: ${isSelected ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
                    color: ${tokens.color.morrisIndigo};
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-height: 52px;
                    transition: all 0.15s ease;
                  "
                >
                  <span style="
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: ${isSelected ? tokens.color.morrisIndigo : 'rgba(7, 20, 36, 0.08)'};
                    color: ${isSelected ? '#FFFDF9' : tokens.color.morrisIndigo};
                    font-size: 0.9rem;
                    font-weight: 800;
                    flex-shrink: 0;
                  ">
                    ${letters[idx]}
                  </span>
                  <span>${opt}</span>
                </button>
              `;
            }).join('')}
          </div>

          <!-- Question Navigation Pager -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 28px; padding-top: 20px; border-top: 1.5px solid rgba(7, 20, 36, 0.1);">
            <button
              id="btn-prev-q"
              class="touch-target"
              style="padding: 10px 20px; font-size: 0.95rem; font-weight: 700; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; cursor: pointer;"
              ${this.currentQuestionIndex === 0 ? 'disabled style="opacity: 0.4;"' : ''}
            >
              &larr; Previous Question
            </button>

            <span style="font-weight: 700; color: #4A5B73; font-size: 0.95rem;">
              Question ${this.currentQuestionIndex + 1} of ${questions.length}
            </span>

            <button
              id="btn-next-q"
              class="touch-target"
              style="padding: 10px 20px; font-size: 0.95rem; font-weight: 700; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; cursor: pointer;"
              ${this.currentQuestionIndex === questions.length - 1 ? 'disabled style="opacity: 0.4;"' : ''}
            >
              Next Question &rarr;
            </button>
          </div>
        </section>

        <!-- Right Side: OMR Answer Sheet Mount -->
        <aside id="mock-omr-mount" style="position: sticky; top: 20px;"></aside>
      </div>
    `;

    // Mount Disc Timer
    const discMount = this.container.querySelector<HTMLElement>('#mock-disc-mount');
    if (discMount && this.discTimer) {
      discMount.appendChild(this.discTimer.getElement());
    }
    this.updateTimerDisplay();

    // Mount OMR Sheet
    const omrMount = this.container.querySelector<HTMLElement>('#mock-omr-mount');
    if (omrMount) {
      this.activeOmrSheet = new OmrSheet({
        totalQuestions: questions.length,
        questionIds: questions.map((item) => item.id),
        currentQuestionIndex: this.currentQuestionIndex,
        initialAnswers: this.currentPaper === 'paper1' ? this.paper1Answers : this.paper2Answers,
        onSelectAnswer: (qIdx, optIdx) => {
          this.handleOptionSelect(qIdx, optIdx);
        },
        onJumpToQuestion: (qIdx) => {
          this.jumpToQuestion(qIdx);
        },
      });
      omrMount.appendChild(this.activeOmrSheet.getElement());
    }

    this.attachActivePaperListeners();
  }

  private attachActivePaperListeners(): void {
    // Zen Mode toggle
    const zenBtn = this.container.querySelector<HTMLButtonElement>('#mock-zen-toggle');
    zenBtn?.addEventListener('click', () => {
      this.isZenMode = !this.isZenMode;
      soundEngine.playTap();
      this.updateTimerDisplay();
      if (zenBtn) zenBtn.textContent = this.isZenMode ? '🧘 Zen: ON' : '⏱️ Zen: OFF';
    });

    // Option Buttons in Question Card
    const optionBtns = this.container.querySelectorAll<HTMLButtonElement>('.mock-option-btn');
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        soundEngine.playTap();
        const optIdx = Number(btn.getAttribute('data-option-idx'));
        const questions = this.currentPaper === 'paper1' ? this.pool.paper1 : this.pool.paper2;
        const q = questions[this.currentQuestionIndex];
        if (!q) return;

        const currentAns = (this.currentPaper === 'paper1' ? this.paper1Answers : this.paper2Answers)[q.id];
        const newAns = currentAns === optIdx ? null : optIdx;

        this.handleOptionSelect(this.currentQuestionIndex, newAns);
      });
    });

    // Pager Next / Prev
    const prevBtn = this.container.querySelector<HTMLButtonElement>('#btn-prev-q');
    prevBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      if (this.currentQuestionIndex > 0) {
        this.jumpToQuestion(this.currentQuestionIndex - 1);
      }
    });

    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-next-q');
    nextBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const questions = this.currentPaper === 'paper1' ? this.pool.paper1 : this.pool.paper2;
      if (this.currentQuestionIndex < questions.length - 1) {
        this.jumpToQuestion(this.currentQuestionIndex + 1);
      }
    });

    // Submit Paper button
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#btn-submit-paper');
    submitBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      if (this.currentPaper === 'paper1') {
        this.submitPaper1(false);
      } else {
        this.submitPaper2(false);
      }
    });
  }

  private handleOptionSelect(questionIndex: number, optionIndex: number | null): void {
    const questions = this.currentPaper === 'paper1' ? this.pool.paper1 : this.pool.paper2;
    const q = questions[questionIndex];
    if (!q) return;

    if (this.currentPaper === 'paper1') {
      this.paper1Answers[q.id] = optionIndex;
    } else {
      this.paper2Answers[q.id] = optionIndex;
    }

    // Sync OMR sheet
    if (this.activeOmrSheet) {
      this.activeOmrSheet.setAnswer(questionIndex, optionIndex);
    }

    // Record Telemetry
    TelemetryCollector.recordEvent({
      questionId: q.id,
      domain: this.currentPaper,
      topic: q.topic,
      durationMs: Date.now() - this.questionStartTime,
      correct: optionIndex === q.correctOptionIndex,
      sessionElapsedSeconds: this.paperDurationSeconds - this.paperTimerRemaining,
    });

    // Re-render question card if it's the current question
    if (questionIndex === this.currentQuestionIndex) {
      this.render();
    }
  }

  private jumpToQuestion(qIndex: number): void {
    this.currentQuestionIndex = qIndex;
    this.questionStartTime = Date.now();
    if (this.activeOmrSheet) {
      this.activeOmrSheet.setCurrentQuestion(qIndex);
    }
    this.render();
  }

  /**
   * Submit Paper 1, lock it permanently, and advance to Restorative Break
   * (FR-MOCK-001, T-12.1.2: Enforce no-backtrack section rules)
   */
  submitPaper1(forced: boolean = false): void {
    if (this.isPaper1Locked) return;

    if (this.paperTimerInterval) {
      clearInterval(this.paperTimerInterval);
      this.paperTimerInterval = null;
    }
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }

    this.paper1TimeSpentSeconds = Math.max(1, Math.round((Date.now() - this.paper1StartTime) / 1000));
    // SEAL PAPER 1 (NO BACKTRACKING)
    this.isPaper1Locked = true;
    this.state = 'break';

    // Start 10-min restorative break timer
    this.startBreakCountdown();
    this.render();

    if (this.hud) {
      this.hud.say(
        forced
          ? 'Time has expired for Paper 1! It is now sealed. Breathe and refresh during your 10-minute break.'
          : 'Paper 1 is sealed! Excellent concentration, Tudor. Take your restorative 10-minute break.',
        'celebrating'
      );
    }
  }

  private startBreakCountdown(): void {
    this.breakTimerRemaining = this.breakDurationSeconds;
    if (this.breakTimerInterval) {
      clearInterval(this.breakTimerInterval);
    }

    this.breakTimerInterval = setInterval(() => {
      if (this.breakTimerRemaining > 0) {
        this.breakTimerRemaining -= 1;
        const breakEl = this.container.querySelector<HTMLElement>('#mock-break-timer');
        if (breakEl) {
          const m = Math.floor(this.breakTimerRemaining / 60);
          const s = this.breakTimerRemaining % 60;
          breakEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
      } else {
        if (this.breakTimerInterval) {
          clearInterval(this.breakTimerInterval);
          this.breakTimerInterval = null;
        }
        soundEngine.playSuccess();
        this.startPaper('paper2');
      }
    }, 1000);
  }

  /** Stage 3: Restorative Mid-Exam Break (FR-MOCK-001) */
  private renderBreakScreen(): void {
    AppShell.setFocusMode(false);
    const m = Math.floor(this.breakTimerRemaining / 60);
    const s = this.breakTimerRemaining % 60;

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="max-width: 680px; margin: 30px auto; padding: 44px 32px; text-align: center; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.15);">
        <!-- Restorative Oceanic Breathing Pulse Circle -->
        <div style="position: relative; width: 140px; height: 140px; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center;">
          <div style="
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(0, 229, 255, 0.25) 0%, rgba(229, 169, 60, 0.15) 100%);
            border: 2px solid ${tokens.color.biolumCyan};
            animation: breathePulse 4s ease-in-out infinite alternate;
          "></div>
          <span style="font-size: 3.5rem; position: relative; z-index: 2;">🪸</span>
        </div>

        <style>
          @keyframes breathePulse {
            0% { transform: scale(0.9); opacity: 0.6; }
            100% { transform: scale(1.15); opacity: 1; }
          }
        </style>

        <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px;">
          ⚓ Paper 1 Sealed · Mid-Exam Rest
        </div>

        <h2 style="font-family: ${tokens.font.heading}; font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 12px;">
          Take a Restorative Breath, Tudor
        </h2>

        <p style="font-size: 1.1rem; line-height: 1.6; color: #3A4D66; max-width: 520px; margin: 0 auto 24px;">
          You have conquered 50 questions of Verbal & English! Sip some water, roll your shoulders, and rest your eyes before Paper 2.
        </p>

        <!-- Digital Break Countdown -->
        <div style="margin-bottom: 28px; padding: 14px 24px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; display: inline-block;">
          <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 2px;">
            Break Time Remaining
          </div>
          <div id="mock-break-timer" style="font-family: ${tokens.font.mono}; font-size: 2.2rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
            ${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}
          </div>
        </div>

        <div>
          <button
            id="btn-ready-paper2"
            class="btn-primary touch-target"
            style="padding: 16px 36px; font-size: 1.15rem; font-weight: 800; border-radius: 16px;"
          >
            I am Refreshed · Begin Paper 2 (Maths & NVR) &rarr;
          </button>
        </div>
      </section>
    `;

    const resumeBtn = this.container.querySelector<HTMLButtonElement>('#btn-ready-paper2');
    resumeBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      if (this.breakTimerInterval) {
        clearInterval(this.breakTimerInterval);
        this.breakTimerInterval = null;
      }
      this.startPaper('paper2');
    });
  }

  /**
   * Submit Paper 2, finalize mock exam, evaluate score, and display results
   * (FR-MOCK-001, FR-MOCK-003, T-12.1.4)
   */
  submitPaper2(forced: boolean = false): void {
    if (this.isPaper2Locked) return;

    if (this.paperTimerInterval) {
      clearInterval(this.paperTimerInterval);
      this.paperTimerInterval = null;
    }
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }

    this.paper2TimeSpentSeconds = Math.max(1, Math.round((Date.now() - this.paper2StartTime) / 1000));
    this.isPaper2Locked = true;
    this.state = 'results';

    // Evaluate full exam using MockExamStorage
    this.currentRecord = MockExamStorage.evaluateExam({
      paper1Answers: this.paper1Answers,
      paper1Questions: this.pool.paper1.map((q) => ({
        id: q.id,
        section: q.section as 'verbal' | 'english',
        correctOptionIndex: q.correctOptionIndex,
      })),
      paper1TimeSpentSeconds: this.paper1TimeSpentSeconds,
      paper2Answers: this.paper2Answers,
      paper2Questions: this.pool.paper2.map((q) => ({
        id: q.id,
        section: q.section as 'numerical' | 'nvr',
        correctOptionIndex: q.correctOptionIndex,
      })),
      paper2TimeSpentSeconds: this.paper2TimeSpentSeconds,
    });

    // Record activity in StreakManager
    StreakManager.recordActivity();

    this.render();

    if (this.hud) {
      const qStatus = this.currentRecord.isQualifying ? 'Qualifying Standard Achieved!' : 'Valiant effort on a full mock!';
      const prefix = forced ? 'Time has expired for Paper 2! ' : '';
      this.hud.say(`${prefix}Mock Exam Complete! ${qStatus} Aggregate SAS: ${this.currentRecord.aggregateSas}.`, 'celebrating');
    }
  }

  /** Stage 4: Comprehensive Mock Exam Results Screen (FR-MOCK-003) */
  private renderResultsScreen(): void {
    AppShell.setFocusMode(false);
    if (!this.currentRecord) return;

    const r = this.currentRecord;
    const isQualifying = r.isQualifying;

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="max-width: 920px; margin: 20px auto; padding: 40px 32px; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.15); text-align: center;">
        <div style="font-size: 3.5rem; margin-bottom: 12px;">🏛️ 📜 ⭐</div>

        <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 6px;">
          Bexley Selection Test · Full Mock Evaluation (${r.dateFormatted})
        </div>

        <h1 style="font-family: ${tokens.font.heading}; font-size: 2.5rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 12px;">
          Mock Exam Results
        </h1>

        <!-- Qualifying Badge -->
        <div style="margin-bottom: 28px;">
          <span style="
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 24px;
            border-radius: 24px;
            font-size: 1.1rem;
            font-weight: 800;
            background: ${isQualifying ? 'rgba(61, 90, 69, 0.15)' : 'rgba(255, 107, 74, 0.15)'};
            color: ${isQualifying ? tokens.color.seaweedSage : tokens.color.madderCoral};
            border: 2px solid ${isQualifying ? tokens.color.seaweedSage : tokens.color.madderCoral};
          ">
            ${isQualifying ? '🏆 Qualifying Projected (≥216 SAS Benchmark)' : '🧭 Building Toward Qualifying (216 SAS Target)'}
          </span>
        </div>

        <!-- 3 Scoreboard Pillars: Paper 1, Paper 2, Aggregate SAS -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 18px; margin-bottom: 32px;">
          <!-- Paper 1 -->
          <div style="padding: 22px; background: rgba(255, 253, 249, 0.95); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 16px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Paper 1 · Verbal & English
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 2.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin: 4px 0;">
              ${r.paper1.sas}
            </div>
            <div style="font-size: 0.9rem; color: #5A6D86;">
              ${r.paper1.correctCount} / ${r.paper1.totalQuestions} (${r.paper1.rawPercent}%)
            </div>
          </div>

          <!-- Paper 2 -->
          <div style="padding: 22px; background: rgba(255, 253, 249, 0.95); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 16px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Paper 2 · Maths & Spatial NVR
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 2.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin: 4px 0;">
              ${r.paper2.sas}
            </div>
            <div style="font-size: 0.9rem; color: #5A6D86;">
              ${r.paper2.correctCount} / ${r.paper2.totalQuestions} (${r.paper2.rawPercent}%)
            </div>
          </div>

          <!-- Total Aggregate SAS -->
          <div style="padding: 22px; background: rgba(255, 246, 229, 0.95); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 16px;">
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.gildedBrass}; text-transform: uppercase;">
              Total Aggregate SAS
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 2.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin: 4px 0;">
              ${r.aggregateSas}
            </div>
            <div style="font-size: 0.9rem; color: #5A6D86;">
              Qualifying Benchmark: ${r.qualifyingThreshold}
            </div>
          </div>
        </div>

        <!-- 4-Domain Breakdown Grid -->
        <h3 style="font-family: ${tokens.font.heading}; font-size: 1.5rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 16px; text-align: left;">
          Domain Competency Breakdown
        </h3>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; margin-bottom: 32px; text-align: left;">
          <div style="padding: 16px; background: rgba(255, 253, 249, 0.9); border: 1.5px solid rgba(26, 54, 93, 0.2); border-radius: 12px;">
            <div style="font-weight: 800; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">Verbal Ability</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${r.domainBreakdown.verbal.percent}%</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${r.domainBreakdown.verbal.correct} of ${r.domainBreakdown.verbal.total} correct</div>
          </div>

          <div style="padding: 16px; background: rgba(255, 253, 249, 0.9); border: 1.5px solid rgba(26, 54, 93, 0.2); border-radius: 12px;">
            <div style="font-weight: 800; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">English Comprehension</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${r.domainBreakdown.english.percent}%</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${r.domainBreakdown.english.correct} of ${r.domainBreakdown.english.total} correct</div>
          </div>

          <div style="padding: 16px; background: rgba(255, 253, 249, 0.9); border: 1.5px solid rgba(26, 54, 93, 0.2); border-radius: 12px;">
            <div style="font-weight: 800; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">Numerical Reasoning</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${r.domainBreakdown.numerical.percent}%</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${r.domainBreakdown.numerical.correct} of ${r.domainBreakdown.numerical.total} correct</div>
          </div>

          <div style="padding: 16px; background: rgba(255, 253, 249, 0.9); border: 1.5px solid rgba(26, 54, 93, 0.2); border-radius: 12px;">
            <div style="font-weight: 800; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">Non-Verbal / Spatial</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${r.domainBreakdown.nvr.percent}%</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${r.domainBreakdown.nvr.correct} of ${r.domainBreakdown.nvr.total} correct</div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div style="display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          <button id="btn-mock-replay" class="btn-primary touch-target" style="padding: 14px 28px; font-size: 1.05rem;">
            🔄 Attempt Another Mock &rarr;
          </button>
          <a href="#/dashboard" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 14px; text-decoration: none; color: ${tokens.color.morrisIndigo}; font-weight: 800; display: inline-flex; align-items: center;">
            📊 View Parent Analytics
          </a>
          <a href="#/" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid rgba(26, 54, 93, 0.3); background: #FFFDF9; border-radius: 14px; text-decoration: none; color: ${tokens.color.morrisIndigo}; font-weight: 700; display: inline-flex; align-items: center;">
            🗺️ Back to Map
          </a>
        </div>
      </section>
    `;

    const replayBtn = this.container.querySelector<HTMLButtonElement>('#btn-mock-replay');
    replayBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.resetMock();
    });
  }

  private resetMock(): void {
    this.state = 'lobby';
    this.currentPaper = 'paper1';
    this.currentQuestionIndex = 0;
    this.paper1Answers = {};
    this.paper2Answers = {};
    this.isPaper1Locked = false;
    this.isPaper2Locked = false;
    this.currentRecord = null;
    this.render();
  }
}
